import { DEFAULT_TARGETS, SECONDARY_DEFAULT_TARGETS } from "~/fc/Constants/config-default-targets";
export default class TargetDeterminer {
    constructor(file) {
        this.file = file;
        this.store = $nuxt.$store;
        this.info = this.store.state.info;
    }

    /**
     * Returns the file type string from 
     * the store or the file object.
     * @returns string
     */
    getFileType() {
        const typeFromStore =
            this.store.state.advancedSetting.typeForExt[this.file.ext];
        return typeFromStore
            ? typeFromStore
            : this.file.type
                ? this.file.type
                : this.file.file && this.file.file.type
                    ? this.file.file.type.split("/")[0]
                    : "";
    }

    /**
     * Gets target information from the page
     * API.
     * @returns target object
     */
    getPageDefaultTarget() {
        if (this.info.target) {
            return typeof this.info.target === "string" ||
                this.info.target instanceof String
                ? { ext: this.info.target, slug: this.info.target }
                : this.info.target;
        }

        if (this.store.state.advancedSetting.defaultTarget) {
            return {
                ext: this.store.state.advancedSetting.defaultTarget,
                slug: this.store.state.advancedSetting.defaultTarget,
                name: this.store.state.advancedSetting.defaultTarget,
            };
        }

        return false;
    }

    /**
     * Returns default target information based 
     * on file type.
     * @returns target object
     */
    getTypeDefault(type, source = 'primary') {
        if (!type) return false;
        const infoOperation = $nuxt.$store.state.info.operation
        const operation = infoOperation && ['convert', 'compress', 'merge'].includes(infoOperation) ? infoOperation : 'convert'
        const supportedFormats = $nuxt.$store.state.advancedSetting?.supportedTargets ? $nuxt.$store.state.advancedSetting?.supportedTargets[operation] : null
        const supportedExt = supportedFormats ? {...supportedFormats[this.file.ext]} : false
        if(supportedExt) return { ext: source === 'primary' ? supportedExt.defaultTarget : supportedExt.secondaryDefaultTarget }
        
        /**
         * If the targets API fails or for some other bug the default
         * targets are not found the system will get the statically defined
         * default targets based on the group name.
         * [e.g. This should not happen. Just a precaution, so that the UX does not misbehave.]
         */
        return { ext: source === 'primary' ? DEFAULT_TARGETS[type] : SECONDARY_DEFAULT_TARGETS[type] };
    }

    /**
     * Checks if a target exists in the list of
     * the supported targets of the file.
     * @param {*} format | target object
     * @returns target object
     */
    existsInSupportedTargets(format) {
        return this.supportedTargets && this.supportedTargets.length
            ? this.supportedTargets.find((target) => {
                /**
                 * The logic checks the slug first because
                 * several device targets have the same ext.
                 * but defferent slugs.
                 */
                return target.slug === (format.slug ? format.slug : format.ext)
                    && target.ext === format.ext
            })
            : false
    }


    /**
     * The supportedTargets are supposed to be fetched
     * right after the page load meaning by the time 
     * this method is executed, the getSupportedTargets()
     * method should return instantly.
     * @returns list of supported targets.
     */
    async getSupportedTargets() {
        const supportedTargets = this.store.state.advancedSetting.targetForExt[this.file.ext];
        if (supportedTargets) return Object.values(supportedTargets).flat();
        /**
         * We are checking if the page requires specific source and targets.
         * If so we are confirming that the file supports convertion to the
         * page target if the file extension belongs to allowed extensions.
         */

        const pageDefaultSupportedTargets =
            this.info.source &&
                this.info.target &&
                this.info?.customVariables?.allowedExts?.includes(this.file.ext)
                ? [this.info.target]
                : false;

        if (pageDefaultSupportedTargets) return pageDefaultSupportedTargets;

        await this.store.dispatch("advancedSetting/setTargetForExt", {
            ext: this.file.ext,
        });

        const supportedTargetsFromAPI = await this.store.state.advancedSetting.targetForExt[this.file.ext];
        return supportedTargetsFromAPI && supportedTargetsFromAPI.targets
            ? Object.values(supportedTargetsFromAPI.targets).flat()
            : false;
    }

    /**
     * Specifically for compress or crop operations,
     * the target determination logic needs to check
     * the custom variables and some other page settings.
     * @returns target object
     */
    determineTargetForCompressPage() {
        /** 
         * Return the source file extension as the target extension
         * if the source file extension exists in customVariables's "targets" list
         * or the "targets" list is missing but forceTarget is true.
         */

        if (!this.info.customVariables) return false;

        if (this.info.customVariables.targets) {
            const targets = this.info.customVariables.targets.split(",");
            return targets.includes(this.file.ext)
                ? { ext: this.file.ext, name: this.file.ext }
                : false;
        } else if (this.info.customVariables.forceTarget) {
            return { ext: this.file.ext, name: this.file.ext };
        }

        return false;
    }

    /**
     * We get target information either from the page
     * API or the defaults based on the file type.
     * This method will gradually check the sources,
     * check if a target is supported and return a
     * valid target. It returns null if no target is 
     * supported.
     * @returns target object
     */
    async determine() {
        /**
         * If the page operation is 'compress' or 'crop' we
         * are determining the target a  bit differently.
         */
        if (['compress', 'crop'].includes(this.info.operation) && this.determineTargetForCompressPage()) {
            return this.determineTargetForCompressPage()
        }
        /**
         * the supported targets are needed to crosscheck 
         * if conversion to a target is supported in the
         * FC system.
         */
        this.supportedTargets = await this.getSupportedTargets();
        /**
         * The target mentioned in the page takes the highest
         * priority. The folloing code will determine the 
         * page target and check if the page target is among 
         * the supported targets of the file. If so it will
         * return the page target.
         */
        const pageTarget = this.getPageDefaultTarget()

        if (pageTarget.slug !== this.file.ext && this.existsInSupportedTargets(pageTarget)) return this.existsInSupportedTargets(pageTarget)
        /**
         * The file type is need to determine the default or 
         * the secondary default target based on type. The 
         * following code will get the file type and the default 
         * target based on the type.
         */
        const fileType = this.getFileType();
        let defaultTarget = this.getTypeDefault(fileType)
        /**
         * If the selected file is the same format as the
         * default target, there is a secondary  list of
         * default target. 
         */
        if (defaultTarget.ext === this.file.ext) defaultTarget = this.getTypeDefault(fileType, 'secondary')
        /** 
         * Now that the default target is determined. we
         * need to check if the default target is among 
         * the supported targets of the file. If so it will
         * return the default target.
         */
        if (this.existsInSupportedTargets(defaultTarget)) return this.existsInSupportedTargets(defaultTarget)
        /**
         * None of the page target or the default target
         * is among the supported targets, meaning the 
         * conversion of this file might not be supported
         * in the FC system. 
         * null sill be returned. 
         */
        return null;
    }
}
